import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, ShieldCheck, GraduationCap, NotebookPen, Loader2 } from "lucide-react";

export default function Parent() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/parent/overview").then((r) => {
    setData(r.data);
    const f = {};
    r.data.students.forEach((s) => { f[s.student.id] = { balance: s.balance, risk_percent: s.risk_percent, max_risk_percent: s.max_risk_percent }; });
    setForm(f);
    r.data.students.forEach((s) => api.get(`/parent/assistance/${s.student.id}`).then((rr) => setCaps((c) => ({ ...c, [s.student.id]: rr.data }))).catch(() => {}));
  }).catch((e) => toast.error(apiErr(e)));

  const [caps, setCaps] = useState({});
  const toggleCap = async (sid, capability, granted) => {
    setBusy(true);
    try { await api.post("/capability/grant", { minor_id: sid, capability, granted }); toast.success(`${capability} ${granted ? "authorized" : "revoked"}`); 
      const rr = await api.get(`/parent/assistance/${sid}`); setCaps((c) => ({ ...c, [sid]: rr.data })); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8 text-tc-muted font-mono text-sm">Loading parent dashboard…</div>;

  const saveSettings = async (sid) => {
    setBusy(true);
    try { await api.post("/parent/settings", { student_id: sid, ...form[sid] }); toast.success("Settings updated"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const resetSim = async (sid) => {
    setBusy(true);
    try { const { data } = await api.post("/parent/reset-simulation", { student_id: sid }); toast.success(data.note); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Parent / Admin</h1>
        <p className="text-tc-sub mt-1">Monitor learning progress and manage safe simulation settings. Historical trades are immutable.</p>
      </div>

      {data.students.length === 0 && <div className="tc-card p-8 text-tc-muted text-center">No students linked to this account yet.</div>}

      {data.students.map((s) => {
        const p = s.progress;
        return (
          <div key={s.student.id} className="tc-card p-6 space-y-5" data-testid={`student-${s.student.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading text-xl font-bold">{s.student.name}</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-tc-xp flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> {p.rank} · Level {p.level}</div>
              </div>
              <div className="text-right"><div className="text-[10px] font-mono uppercase text-tc-muted">Simulated Balance</div><div className="font-mono text-xl">${s.balance.toLocaleString()}</div></div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat icon={GraduationCap} l="Lessons" v={s.lessons_completed} />
              <Stat l="Trades" v={s.trades} />
              <Stat l="PASS" v={s.pass_decisions} accent="text-tc-pass" />
              <Stat l="Avg Score" v={`${s.avg_process_score}`} accent="text-tc-mastery" />
              <Stat icon={ShieldCheck} l="Risk OK" v={`${s.risk_compliance}%`} accent="text-tc-bull" />
              <Stat icon={NotebookPen} l="Journaled" v={`${s.journal_completion}%`} accent="text-tc-coach" />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="tc-card p-4 bg-white/[0.02]">
                <div className="text-sm font-semibold mb-1">Strongest Skill</div>
                <div className="text-tc-bull font-mono text-sm">{s.strongest_skill || "—"}</div>
                <div className="text-sm font-semibold mt-3 mb-1">Current Improvement Area</div>
                <div className="text-tc-mastery font-mono text-sm">{s.improvement_area?.title}</div>
                <p className="text-xs text-tc-muted mt-1">{s.improvement_area?.description}</p>
              </div>
              <div className="tc-card p-4 bg-white/[0.02]">
                <div className="text-sm font-semibold mb-2">Recent Rule Violations</div>
                {s.violations.length === 0 ? <p className="text-sm text-tc-muted">None — great discipline.</p> : (
                  <div className="space-y-1.5">{s.violations.slice(0, 5).map((v) => (
                    <div key={v.code} className="flex justify-between text-sm"><span className="text-tc-sub">{v.code.replace(/_/g, " ")}</span><span className="font-mono text-tc-pass">{v.count}</span></div>
                  ))}</div>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="border-t border-white/10 pt-4">
              <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-3">Simulation Settings</div>
              <div className="grid sm:grid-cols-4 gap-3 items-end">
                <Field label="Starting Balance ($)" value={form[s.student.id]?.balance}
                  onChange={(v) => setForm({ ...form, [s.student.id]: { ...form[s.student.id], balance: +v } })} testid={`set-balance-${s.student.id}`} />
                <Field label="Default Risk (%)" value={form[s.student.id]?.risk_percent} step="0.5"
                  onChange={(v) => setForm({ ...form, [s.student.id]: { ...form[s.student.id], risk_percent: +v } })} testid={`set-risk-${s.student.id}`} />
                <Field label="Max Risk (%)" value={form[s.student.id]?.max_risk_percent} step="0.5"
                  onChange={(v) => setForm({ ...form, [s.student.id]: { ...form[s.student.id], max_risk_percent: +v } })} testid={`set-maxrisk-${s.student.id}`} />
                <div className="flex gap-2">
                  <button data-testid={`save-settings-${s.student.id}`} disabled={busy} onClick={() => saveSettings(s.student.id)}
                    className="flex-1 rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 text-sm hover:bg-tc-coach/90 disabled:opacity-50">Save</button>
                </div>
              </div>
              <button data-testid={`reset-sim-${s.student.id}`} disabled={busy} onClick={() => resetSim(s.student.id)}
                className="mt-3 text-xs text-tc-muted hover:text-tc-bear flex items-center gap-1.5">
                {busy && <Loader2 className="w-3 h-3 animate-spin" />} Reset simulated balance to $10,000 (history preserved)
              </button>
            </div>

            {/* AI capability authorization (guardian) */}
            {caps[s.student.id] && (
              <div className="border-t border-white/10 pt-4" data-testid={`caps-${s.student.id}`}>
                <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-1">AI Assistance Authorization</div>
                <p className="text-xs text-tc-muted mb-3">Current level: <span className="text-tc-coach font-mono">{caps[s.student.id].current}</span>. Gated capabilities require both demonstrated competency and your authorization.</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {caps[s.student.id].capabilities.filter((c) => c.needs_guardian).map((c) => (
                    <div key={c.capability} className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                      <div>
                        <div className="text-sm font-semibold">{c.capability}</div>
                        <div className="text-[11px] text-tc-muted">{c.eligible ? "Competency met" : "Competency not yet met"}{c.future ? " · future" : ""}</div>
                      </div>
                      <button data-testid={`grant-${c.capability}-${s.student.id}`} disabled={busy || c.future}
                        onClick={() => toggleCap(s.student.id, c.capability, !c.guardian_granted)}
                        className={`text-xs font-mono px-3 py-1.5 rounded-lg border ${c.guardian_granted ? "bg-tc-bull/15 text-tc-bull border-tc-bull/40" : "bg-white/5 text-tc-sub border-white/10 hover:border-white/30"} disabled:opacity-40`}>
                        {c.guardian_granted ? "Authorized" : "Authorize"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Stat({ icon: Icon, l, v, accent = "text-tc-txt" }) {
  return (
    <div className="tc-card p-3 bg-white/[0.02]">
      <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{l}</div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${accent}`}>{v}</div>
    </div>
  );
}
function Field({ label, value, onChange, step = "1", testid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase text-tc-muted">{label}</label>
      <input data-testid={testid} type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm font-mono outline-none focus:border-tc-coach" />
    </div>
  );
}
